import { useEffect, useState } from "react"
import { useUserContext } from "../../contexts/UserContext"
import { useContentfulContext } from "../../contexts/ContentfulContext"
import { formatDate } from "../../utils/converter"
import { getOrgPlanMappings, cancelSubscription, getStripePortalSession, getPaymentStatus, toggleAutoRenew } from "../../utils/APIs"
import Loader from "../UI/Loader/Loader"
import useComponentVisible from "../UI/ComponentVisible"
import UsageDetails from "./UsageDetails"
import TransactionHistory from "../Stripe/TransactionHistory"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import EmbeddedBilling from "../Payment/EmbeddedBilling"
import ConfirmModal from "../UI/Modal/confirmModal"
import Toggle from "../UI/Toggle"

const MyActivePlan = () => {
    const navigate = useNavigate()
    const { userData, updateSelectedOrgData } = useUserContext()
    const { content } = useContentfulContext()
    const [organizationPlans, setOrganizationPlans] = useState([])
    const [loading, setLoading] = useState(true)
    const [cancelling, setCancelling] = useState(false)
    const [paymentStatus, setPaymentStatus] = useState({ hasValidCard: false, autoRenew: false })
    const [updatingAutoRenew, setUpdatingAutoRenew] = useState(false)
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false)
    const [showBillingModal, setShowBillingModal] = useState(false)
    const [openConfirmCancel, setOpenConfirmCancel] = useState(false);

    const fetchOrganizationPlans = async () => {
        setLoading(true)
        try {
            const plans = await getOrgPlanMappings(userData?.token, userData?.selectedOrganization?.org_token, true)
            if (plans.status) {
                setOrganizationPlans(plans.data[0])
            }

            // Fetch payment status
            const status = await getPaymentStatus(userData.token, userData?.selectedOrganization?.org_token);
            if (status.status) {
                setPaymentStatus(status.data);
            }
        } catch (error) {
            console.error("Failed to fetch organization plans or payment status:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (userData?.selectedOrganization?.org_token) {
            fetchOrganizationPlans()
        }
    }, [userData?.selectedOrganization])

    const handleCancelSubscription = async () => {
        try {
            setCancelling(true);
            const res = await cancelSubscription(userData.token, userData?.selectedOrganization?.org_token);
            if (res.status) {
                toast.success("Subscription cancelled successfully.");
                setOpenConfirmCancel(false);
                await updateSelectedOrgData();
                await fetchOrganizationPlans();
                setIsComponentVisible(false);
            } else {
                toast.error(res.message || "Failed to cancel subscription.");
            }
        } catch (error) {
            console.error("Cancel subscription error:", error);
            toast.error(error?.message || "An error occurred while cancelling your subscription.");
        } finally {
            setCancelling(false);
        }
    }

    const handleManageBilling = async () => {
        setShowBillingModal(true);
        setIsComponentVisible(false);
    }

    const handleToggleAutoRenew = async () => {
        try {
            setUpdatingAutoRenew(true);
            const newAutoRenew = !paymentStatus.autoRenew;
            const res = await toggleAutoRenew(userData.token, userData?.selectedOrganization?.org_token, newAutoRenew);
            if (res.status) {
                setPaymentStatus({ ...paymentStatus, autoRenew: newAutoRenew });
                toast.success(`Auto-renewal ${newAutoRenew ? 'enabled' : 'disabled'} successfully.`);
            } else {
                toast.error(res.message || "Failed to update auto-renewal.");
            }
        } catch (error) {
            console.error("Toggle auto-renew error:", error);
            toast.error("An error occurred while updating auto-renewal.");
        } finally {
            setUpdatingAutoRenew(false);
        }
    }


    if (loading) {
        return <Loader />
    }

    return (
        <>
            {showBillingModal &&
                <BillingModal
                    onClose={() => setShowBillingModal(false)}
                    subscription={organizationPlans}
                    onRefresh={fetchOrganizationPlans}
                />
            }
            {openConfirmCancel && <ConfirmModal
                title={"Cancel Subscription"}
                description={"Are you sure you want to cancel your subscription? Your access will remain until the end of the current billing period."}
                hideModal={() => setOpenConfirmCancel(false)}
                ConfirmText={"Cancel Subscription"}
                CancelText={"Cancel"}
                handleConfirmation={handleCancelSubscription}
            />}
            <main id="main" className="flex flex-col space-y-4 my-6 md:px-8 px-2">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center">
                        <div>
                            <h1 className="text-xl font-medium leading-tight tracking-tight text-gray-900 md:text-2xl">
                                {content.OWNER_PLAN.title}
                            </h1>
                            <p>{content.OWNER_PLAN.description}</p>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/* Actions Dropdown */}
                            {!organizationPlans?.plan_id?.is_custom_plan && <div className="relative" ref={ref} id="my-plan-actions-dropdown-wrapper">
                                <button
                                    id="my-plan-actions-dropdown-btn"
                                    onClick={() => setIsComponentVisible(!isComponentVisible)}
                                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary-2 focus:ring-opacity-50"
                                >
                                    {content.OWNER_PLAN.actions}
                                    <svg
                                        className={`ml-2 w-4 h-4 inline-block transition-transform duration-200 ${isComponentVisible ? "rotate-180" : ""}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>

                                {isComponentVisible && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                        <div className="py-1">
                                            <button
                                                id="upgrade-plan-dropdown-option"
                                                onClick={() => navigate("/pricing")}
                                                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                            >
                                                {content.OWNER_PLAN.cta_text.upgrade_plan}
                                            </button>
                                            <button
                                                onClick={handleManageBilling}
                                                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                            >

                                                <span>Manage Billing & Transactions</span>
                                            </button>
                                            <button
                                                onClick={() => setOpenConfirmCancel(true)}
                                                disabled={cancelling}
                                                className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100 disabled:opacity-50"
                                            >
                                                {cancelling ? "Cancelling..." : "Cancel Subscription"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>}
                        </div>
                    </div>

                    <div className="p-6">
                        {organizationPlans?.status === 'failed' && organizationPlans?.failure_message && (
                            <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-start gap-3 shadow-sm">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-red-800">Subscription Billing Error</h3>
                                    <p className="text-sm text-red-700 mt-1 font-medium">
                                        {organizationPlans.failure_message}
                                    </p>
                                    <button
                                        onClick={handleManageBilling}
                                        className="mt-2 text-xs font-bold text-red-800 underline hover:text-red-900"
                                    >
                                        Update Payment Method
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div className="space-y-4">
                                <div>
                                    <h2 className="text-sm font-medium text-gray-500">{content.OWNER_PLAN.plan_name}</h2>
                                    <p className="text-lg font-medium">
                                        {organizationPlans?.plan_id?.plan_name}
                                    </p>
                                </div>

                                <div>
                                    <h2 className="text-sm font-medium text-gray-500">{content.OWNER_PLAN.start_date}</h2>
                                    <p className="text-lg font-medium">
                                        {organizationPlans?.start_date ? formatDate(organizationPlans?.start_date) : "N/A"}
                                    </p>
                                </div>

                                <div>
                                    <h2 className="text-sm font-medium text-gray-500">{content.OWNER_PLAN.end_date}</h2>
                                    <p className="text-lg font-medium">
                                        {organizationPlans?.end_date ? formatDate(organizationPlans?.end_date) : "N/A"}
                                    </p>
                                </div>
                            </div>
                            {/* Auto-renew Toggle */}
                            {!organizationPlans?.plan_id?.is_custom_plan && <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 flex flex-col justify-center">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900">Auto-renewal</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {paymentStatus.hasValidCard
                                                ? "Your subscription will automatically renew at the end of the billing period."
                                                : "Please set up a valid card in billing to enable auto-renewal."}
                                        </p>
                                    </div>
                                    <div className="flex items-center">
                                        {updatingAutoRenew ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                                        ) : (
                                            <Toggle
                                                id="auto-renew-toggle"
                                                checked={paymentStatus.autoRenew}
                                                disabled={!paymentStatus.hasValidCard || updatingAutoRenew}
                                                onChange={handleToggleAutoRenew}
                                                size="md"
                                            />
                                        )}
                                    </div>
                                </div>
                                {!paymentStatus.hasValidCard && (
                                    <button
                                        onClick={handleManageBilling}
                                        className="mt-4 text-sm font-medium text-primary hover:underline flex items-center"
                                    >
                                        Set up payment method
                                        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </button>
                                )}
                            </div>}
                        </div>

                        <div className="mb-8">
                            <h2 className="text-xl font-semibold mb-4">{content.OWNER_PLAN?.usage}</h2>
                            <UsageDetails usageData={organizationPlans?.usage} />
                        </div>
                    </div>
                </div>
            </main>
        </>
    )
}

const BillingModal = ({ onClose, subscription, onRefresh }) => {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col relative overflow-hidden">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 bg-white rounded-full p-1 shadow-sm"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="flex-1 overflow-y-auto">
                    <EmbeddedBilling subscription={subscription} onRefresh={onRefresh} />
                </div>
            </div>
        </div>
    );
};

export default MyActivePlan